/** Pelota, Petardos e Faísca na cena (item 4.2). Posições vêm da simulação, interpoladas entre ticks. */
import * as THREE from "three";
import { INK, ramp } from "@besenliga/athlete-three";
import { BALL } from "@besenliga/shared";
import type { BallsState, Vec3 } from "@besenliga/sim";

const COLORS = { pelota: 0xd2452f, petardo: 0x2e2a35, faisca: 0xf2c14e, glow: 0xffd36b } as const;

function toonBall(radius: number, color: number): THREE.Group {
  const g = new THREE.Group();
  const geo = new THREE.IcosahedronGeometry(radius, 2);
  const m = new THREE.Mesh(geo, new THREE.MeshToonMaterial({ color, gradientMap: ramp }));
  m.castShadow = true;
  const o = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color: INK, side: THREE.BackSide }));
  o.scale.setScalar(1.12);
  g.add(m, o);
  return g;
}

function glowTexture(): THREE.Texture {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d");
  if (g) {
    const r = g.createRadialGradient(32, 32, 0, 32, 32, 32);
    r.addColorStop(0, "rgba(255,236,170,1)");
    r.addColorStop(0.35, "rgba(255,200,80,0.55)");
    r.addColorStop(1, "rgba(255,200,80,0)");
    g.fillStyle = r;
    g.fillRect(0, 0, 64, 64);
  }
  return new THREE.CanvasTexture(c);
}

const lerp = (a: Vec3, b: Vec3, t: number, out: THREE.Vector3) =>
  out.set(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);

export class Balls3D {
  readonly root = new THREE.Group();
  private readonly pelota = toonBall(BALL.pelota.radius, COLORS.pelota);
  private readonly petardos = [
    toonBall(BALL.petardo.radius, COLORS.petardo),
    toonBall(BALL.petardo.radius, COLORS.petardo),
  ];
  private readonly faisca = new THREE.Group();
  private readonly wings: THREE.Mesh[] = [];
  private readonly glow: THREE.Sprite;

  constructor() {
    this.root.name = "Bolas";
    const core = toonBall(BALL.faisca.radius, COLORS.faisca);
    const wingGeo = new THREE.PlaneGeometry(0.7, 0.28);
    wingGeo.translate(0.38, 0, 0);
    const wingMat = new THREE.MeshBasicMaterial({
      color: 0xfff4d6,
      transparent: true,
      opacity: 0.85,
      side: THREE.DoubleSide,
    });
    for (const side of [1, -1]) {
      const w = new THREE.Mesh(wingGeo, wingMat);
      w.scale.x = side;
      this.wings.push(w);
      core.add(w);
    }
    this.glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTexture(),
        color: COLORS.glow,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    this.glow.scale.setScalar(2.4);
    this.faisca.add(core, this.glow);
    this.faisca.visible = false;
    this.root.add(this.pelota, ...this.petardos, this.faisca);
  }

  /**
   * `heldAt`: onde desenhar a Pelota quando alguém a carrega (a mão do atleta, já interpolada); sem ela a bola
   * segue a posição da simulação.
   */
  update(prev: BallsState, cur: BallsState, alpha: number, time: number, heldAt?: THREE.Vector3): void {
    if (cur.pelota.holder && heldAt) this.pelota.position.copy(heldAt);
    else lerp(prev.pelota.position, cur.pelota.position, alpha, this.pelota.position);
    cur.petardos.forEach((p, i) => {
      const mesh = this.petardos[i];
      const before = prev.petardos[i] ?? p;
      if (mesh) lerp(before.position, p.position, alpha, mesh.position);
    });
    this.faisca.visible = cur.faisca.active;
    if (cur.faisca.active) {
      lerp(prev.faisca.position, cur.faisca.position, alpha, this.faisca.position);
      const flap = Math.sin(time * 38) * 0.9;
      this.wings.forEach((w, i) => (w.rotation.y = (i === 0 ? 1 : -1) * flap));
      const v = cur.faisca.velocity;
      if (v.x * v.x + v.z * v.z > 0.1) this.faisca.rotation.y = Math.atan2(v.x, v.z);
      this.glow.material.opacity = 0.75 + Math.sin(time * 9) * 0.2;
    }
  }

  dispose(): void {
    this.root.traverse((o) => {
      if (o instanceof THREE.Mesh || o instanceof THREE.Sprite) {
        o.geometry.dispose();
        const m = o.material as THREE.Material & { map?: THREE.Texture | null };
        m.map?.dispose();
        m.dispose();
      }
    });
    this.root.removeFromParent();
  }
}
